import React, { useEffect, useState } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from 'shared/atoms/Button';
import HorizontalRule from 'shared/atoms/HorizontalRule';
import SearchField from 'shared/atoms/SearchField';
import T from 'shared/atoms/Typography';

import theme from 'shared/themes/default';
import { invitationStatus, invitationSource } from 'shared/const';
import { downloadFile } from 'shared/utils';

import { useOrganizationMembers } from '../../context';

import FilterDropdown from '../FilterDropdown';
import InvitedMembersTable from '../InvitedMembersTable';
import Totals from '../Totals';

const StyledExportBlock = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 75%;
  margin: 1rem 1rem 1rem 0;
  ${theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const StyledInvitedTableWrapper = styled.div`
  margin: 1rem 0 1rem 0 1rem;
  padding-bottom: 1.5rem;
`;

const StyledBlock = styled.div`
  min-width: 25%;
  ${theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const StyledFilterWrapper = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${theme.breakpoints.mobile} {
    display: block;
  }
`;

const filterOptions = (t) => [
  { id: invitationStatus.pending, label: t('administrateUsers.queued') },
  { id: invitationStatus.invited, label: t('administrateUsers.unanswered') },
  { id: invitationStatus.bounced, label: t('administrateUsers.bounced') },
  {
    id: invitationSource.client,
    label: t('administrateUsers.invitedFromClient'),
  },
  {
    id: invitationSource.backoffice,
    label: t('administrateUsers.invitedFromBackoffice'),
  },
  {
    id: invitationSource.import,
    label: t('administrateUsers.invitedFromImport'),
  },
];

const Invited = () => {
  const { t } = useTranslation();

  const [filteredInvitedMembers, setFilteredInvitedMembers] = useState();
  const [searchedInvitedMembers, setSearchedInvitedMembers] = useState();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedActionText, setSelectedActionText] = useState();
  const [loadingExport, setLoadingExport] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionOptions, setActionOptions] = useState([]);

  const {
    downloadInvitations,
    invitations: {
      deleteInvitations,
      invitationsFormStatus,
      invitedMembers,
      resendInvitations,
      setFailureList,
      setInvitationsFormStatus,
      setShowMultipleFeedback,
      setSuccessList,
      showMultipleFeedback,
    },
    isManager,
    selectedAction,
    setSelectedAction,
    totalLicences,
  } = useOrganizationMembers();

  const { totalInvitedMembers, totalBounced, totalQueued, totalUnanswered } =
    invitedMembers.reduce(
      (
        {
          totalBounced: oldTotalBounced,
          totalInvitedMembers: oldTotalInvitedMembers,
          totalQueued: oldTotalQueued,
          totalUnanswered: oldTotalUnanswered,
        },
        member
      ) => {
        let totalBounced = oldTotalBounced;
        const totalInvitedMembers = oldTotalInvitedMembers + 1;
        let totalQueued = oldTotalQueued;
        let totalUnanswered = oldTotalUnanswered;

        switch (member.status) {
          case invitationStatus.bounced:
            totalBounced += 1;
            break;
          case invitationStatus.invited:
            totalUnanswered += 1;
            break;
          case invitationStatus.pending:
            totalQueued += 1;
            break;
          default:
            break;
        }
        return {
          totalBounced,
          totalInvitedMembers,
          totalQueued,
          totalUnanswered,
        };
      },
      {
        totalInvitedMembers: 0,
        totalBounced: 0,
        totalQueued: 0,
        totalUnanswered: 0,
      }
    );

  const totalsData = {
    [t('administrateUsers.bounced')]: totalBounced,
    [t('administrateUsers.queued')]: totalQueued,
    [t('administrateUsers.unanswered')]: totalUnanswered,
  };

  useEffect(() => {
    setActionOptions([
      { value: 'remove', label: t('administrateUsers.removeInvitations') },
      ...(totalLicences
        ? [
            {
              value: 'resend',
              label: t('administrateUsers.resendInvitations'),
            },
          ]
        : []),
    ]);
  }, [totalLicences]);

  const downloadReport = async () => {
    try {
      setLoadingExport(true);

      // Call backend to generate the file and return the required info for building the download URL
      const { data } = await downloadInvitations();
      downloadFile(data.signedUrl, data.fileName);
      setLoadingExport(false);
    } catch (error) {
      setLoadingExport(false);
      console.error('Some error happened when building the export file', error);
    }
  };

  const onFilterChange = (values) => {
    const isStatusFilter =
      values.includes(invitationStatus.pending) ||
      values.includes(invitationStatus.bounced) ||
      values.includes(invitationStatus.invited);
    const isSourceFilter =
      values.includes(invitationSource.client) ||
      values.includes(invitationSource.backoffice) ||
      values.includes(invitationSource.import);

    if (values.length > 0) {
      let filteredInvitedMembers = null;
      if (isStatusFilter) {
        filteredInvitedMembers = invitedMembers.filter(
          (item) => item.status && values.includes(item.status)
        );
      }

      if (isSourceFilter) {
        filteredInvitedMembers = (
          filteredInvitedMembers || invitedMembers
        ).filter((item) => item.source && values.includes(item.source));
      }

      setFilteredInvitedMembers(filteredInvitedMembers);
    } else {
      setFilteredInvitedMembers(null);
    }

    setSelectedFilters(values);
  };

  const onSearchHandler = ({ value }) => {
    setSearchTerm(value);
    if (value) {
      const searchedInvitedMembers = (
        filteredInvitedMembers || invitedMembers
      ).filter((member) => {
        return (
          member.name.toLowerCase().includes(value.toLowerCase()) ||
          member.email.toLowerCase().includes(value.toLowerCase())
        );
      });
      setSearchedInvitedMembers(searchedInvitedMembers);
    } else {
      setSearchedInvitedMembers(null);
    }
  };

  const onActionChange = (value) => {
    const selectedItem = actionOptions.find((item) => item.value === value);
    setSelectedAction(value);
    setSelectedActionText(selectedItem.label);
  };

  const onActionApplyHandler = (selectedEmails) => {
    if (selectedEmails.length <= 0) {
      return false;
    }
    const updateSelectedUsers = selectedEmails.map((email) => {
      const member = invitedMembers.find((member) => member.email === email);
      if (member) {
        const { email, name, role } = member;
        return { email, name, role };
      }
      // TODO: is this necessary?
      return false;
    });
    if (selectedAction && selectedAction === 'resend') {
      resendInvitations(updateSelectedUsers);
    } else if (selectedAction && selectedAction === 'remove') {
      deleteInvitations(updateSelectedUsers);
    }
    return true;
  };

  const onConfirmationDialogCloseHandler = () => {
    setInvitationsFormStatus(null);
    setShowMultipleFeedback(false);
    setFailureList([]);
    setSuccessList([]);
    setSelectedAction('');
  };

  if (isManager || (!totalInvitedMembers && !showMultipleFeedback)) return null;

  return (
    <>
      <T
        color={theme.palette.lightPure}
        fontWeight="medium"
        sx={{ mb: theme.spacing.s, mt: theme.spacing.m1 }}
        variant="subtitle"
      >
        {t('administrateUsers.invitedMembersSummary', {
          count: totalInvitedMembers,
        })}
      </T>
      {!!totalInvitedMembers && (
        <>
          <Totals data={totalsData} />
          <StyledFilterWrapper>
            <StyledExportBlock>
              <FilterDropdown
                options={filterOptions(t)}
                selectedFilters={selectedFilters}
                onChange={onFilterChange}
              />
              <Button
                ariaLabel={t('administrateUsers.exportAll')}
                onClick={downloadReport}
                loading={loadingExport}
                theme="dark"
                type="primary"
              >
                {t('administrateUsers.exportAll')}
              </Button>
            </StyledExportBlock>
            <StyledBlock>
              <SearchField
                type="secondary"
                placeholder={t('administrateUsers.searchForPeople')}
                ariaLabel={t('administrateUsers.searchForPeople')}
                onChange={onSearchHandler}
                value={searchTerm}
              />
            </StyledBlock>
          </StyledFilterWrapper>
        </>
      )}
      <StyledInvitedTableWrapper>
        <InvitedMembersTable
          invitedMembers={
            searchedInvitedMembers || filteredInvitedMembers || invitedMembers
          }
          selectedAction={selectedAction}
          selectedActionText={selectedActionText}
          actionOptions={actionOptions}
          onActionChange={onActionChange}
          onActionApplyHandler={onActionApplyHandler}
          onConfirmationDialogCloseHandler={onConfirmationDialogCloseHandler}
          formStatus={invitationsFormStatus}
          showMultipleFeedback={showMultipleFeedback}
          setShowMultipleFeedback={setShowMultipleFeedback}
        />
      </StyledInvitedTableWrapper>
      {!!totalInvitedMembers && <HorizontalRule />}
    </>
  );
};

export default Invited;
